import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Wallet, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, LoadingBlock, EmptyState, formatDate, formatDateTime, formatCurrency } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { walletName } from "@/data/site";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const TABS = [
  { k: "overview", it: "Riepilogo", en: "Overview", de: "Übersicht" },
  { k: "ledger", it: "Ledger", en: "Ledger", de: "Ledger" },
  { k: "deposits", it: "Depositi", en: "Deposits", de: "Einzahlungen" },
  { k: "withdrawals", it: "Prelievi", en: "Withdrawals", de: "Auszahlungen" },
  { k: "wallet", it: "Wallet", en: "Wallet", de: "Wallet" },
  { k: "audit", it: "Audit", en: "Audit", de: "Audit" },
];

export default function AdminClientDetail() {
  const { id } = useParams();
  const { lang, t } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de || en) : it);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const load = () => api.get(`/admin/clients/${id}/full`).then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  if (data === null) return <LoadingBlock />;
  if (data === false) return <EmptyState title={L("Cliente non trovato", "Client not found", "Kunde nicht gefunden")} />;
  const u = data.user;
  const b = data.balance;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success(L("Copiato", "Copied", "Kopiert")); };

  return (
    <div className="space-y-6">
      <Link to="/admin/clients" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{L("Torna ai clienti", "Back to clients", "Zurück zu Kunden")}</Link>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-2xl font-medium text-white" data-testid="cd-name">{u.first_name} {u.last_name}</h2>
          <p className="text-slate-400">{u.email} · {u.phone_prefix || ""} {u.phone || "—"} · {u.country || "—"} · {(u.preferred_language || "it").toUpperCase()}</p>
          <p className="text-xs mt-1 flex items-center gap-2">
            <span data-testid="cd-status" className={u.status === "blocked" ? "text-red-500" : u.status === "suspended" ? "text-amber-400" : u.status === "archived" ? "text-slate-500" : "text-emerald-400"}>{t(`status.account.${u.status}`)}</span>
            <span className="text-slate-600">·</span>
            <span data-testid="cd-verification" className={u.verification_status === "verified" ? "text-emerald-400" : u.verification_status === "rejected" ? "text-red-400" : "text-slate-400"}>{t(`status.verification.${u.verification_status}`)}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">{L("Ultimo accesso", "Last login", "Letzter Login")}: {data.last_login ? formatDateTime(data.last_login) : "—"}</span>
          </p>
          {u.status_reason && <p className="text-xs mt-1 text-red-300">{L("Motivo", "Reason", "Grund")}: {u.status_reason}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label={L("Disponibile", "Available", "Verfügbar")} value={formatCurrency(b.available, b.currency)} testid="cd-available" />
        <Metric label={L("Bloccato", "Reserved", "Reserviert")} value={formatCurrency(b.reserved, b.currency)} testid="cd-reserved" />
        <Metric label={L("In sospeso", "Pending", "Ausstehend")} value={formatCurrency(b.pending, b.currency)} />
        <Metric label={L("Commissione prelievo", "Withdrawal fee", "Auszahlungsgebühr")} value={`${data.withdrawal_config.percent}% + ${data.withdrawal_config.fixed}`} />
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`cd-tab-${t.k}`} className={`px-3 py-2 text-sm rounded-t-md ${tab === t.k ? "text-gold border-b-2 border-gold" : "text-slate-400 hover:text-white"}`}>{t[lang] || t.it}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-heading text-lg text-white mb-3">{L("Note interne", "Internal notes", "Interne Notizen")}</h3>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{u.internal_note || L("Nessuna nota", "No notes", "Keine Notizen")}</p>
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-lg text-white mb-3">{L("Pratiche", "Cases", "Fälle")} ({data.cases.length})</h3>
            {data.cases.length === 0 ? <p className="text-sm text-slate-500">—</p> : (
              <div className="space-y-2">
                {data.cases.map((c) => (
                  <Link key={c.id} to={`/admin/cases/${c.id}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3 hover:border-cyanx/30">
                    <span className="text-sm text-white font-mono">{c.reference} · <span className="text-slate-400">{walletName(c.wallet_type)}</span></span>
                    <StatusBadge status={c.status} />
                  </Link>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-lg text-white mb-3">{L("Accessi recenti", "Recent logins", "Letzte Logins")}</h3>
            {data.login_history.length === 0 ? <p className="text-sm text-slate-500">—</p> : (
              <div className="space-y-1.5">{data.login_history.map((h) => (
                <div key={h.id} className="flex items-center justify-between text-sm border-b border-white/5 py-2"><span className="text-slate-300">{h.ip}</span><span className="text-xs text-slate-600">{formatDateTime(h.created_at)}</span></div>
              ))}</div>
            )}
          </Card>
        </div>
      )}

      {tab === "ledger" && (
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="cd-ledger-table">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase"><tr>
              <th className="text-left px-4 py-3">{L("Data", "Date", "Datum")}</th><th className="text-left px-4 py-3">{L("Tipo", "Type", "Typ")}</th>
              <th className="text-right px-4 py-3">{L("Importo", "Amount", "Betrag")}</th><th className="text-right px-4 py-3">{L("Saldo dopo", "Balance after", "Saldo danach")}</th>
              <th className="text-left px-4 py-3">{L("Stato", "Status", "Status")}</th><th className="text-left px-4 py-3">{L("Motivo", "Reason", "Grund")}</th>
            </tr></thead>
            <tbody>{data.ledger.map((e) => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{formatDate(e.created_at)}</td>
                <td className="px-4 py-2.5 text-slate-300">{e.entry_type}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${e.direction === "credit" ? "text-emerald-400" : e.direction === "debit" ? "text-red-400" : "text-slate-400"}`}>{e.direction === "debit" ? "−" : e.direction === "credit" ? "+" : ""}{formatCurrency(e.amount, e.currency)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-300">{e.balance_after != null ? formatCurrency(e.balance_after, e.currency) : "—"}</td>
                <td className="px-4 py-2.5 text-slate-400">{e.status}</td>
                <td className="px-4 py-2.5 text-slate-500">{e.reason || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
          {data.ledger.length === 0 && <p className="p-6 text-sm text-slate-500">{L("Nessun movimento", "No entries", "Keine Buchungen")}</p>}
        </Card>
      )}

      {tab === "deposits" && <TxList rows={data.deposits} L={L} empty={L("Nessun deposito", "No deposits", "Keine Einzahlungen")} kind="dep" />}
      {tab === "withdrawals" && <TxList rows={data.withdrawals} L={L} empty={L("Nessun prelievo", "No withdrawals", "Keine Auszahlungen")} kind="wd" />}

      {tab === "wallet" && (
        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-heading text-lg text-white mb-3 flex items-center gap-2"><Wallet className="h-5 w-5 text-cyanx" />{L("Wallet assegnato", "Assigned wallet", "Zugewiesenes Wallet")}</h3>
            {data.wallet && data.wallet.address ? (
              <div className="space-y-2 text-sm">
                <p className="text-slate-400">{data.wallet.asset} · {data.wallet.network} · <span className={data.wallet.status === "ACTIVE" ? "text-emerald-400" : "text-slate-500"}>{data.wallet.status}</span></p>
                <div className="flex items-center gap-2"><code className="flex-1 rounded bg-navy border border-white/10 px-3 py-2 font-mono text-xs text-white break-all" data-testid="cd-wallet-address">{data.wallet.address}</code>
                  <Button size="sm" variant="outline" className="border-white/20" onClick={() => copy(data.wallet.address)}><Copy className="h-4 w-4" /></Button></div>
              </div>
            ) : <p className="text-sm text-slate-500">{L("Nessun wallet assegnato", "No wallet assigned", "Kein Wallet zugewiesen")}</p>}
          </Card>
          <Card className="p-6">
            <h3 className="font-heading text-lg text-white mb-3">{L("Storico wallet", "Wallet history", "Wallet-Verlauf")}</h3>
            {data.wallet_history.length === 0 ? <p className="text-sm text-slate-500">—</p> : (
              <div className="space-y-1.5">{data.wallet_history.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-sm border-b border-white/5 py-2"><span className="text-slate-300 font-mono text-xs break-all">{w.asset} · {w.network} · {w.address}</span><span className="text-xs text-slate-600 shrink-0 ml-2">{formatDate(w.created_at)}</span></div>
              ))}</div>
            )}
          </Card>
        </div>
      )}

      {tab === "audit" && (
        <Card className="p-6">
          <h3 className="font-heading text-lg text-white mb-3">{L("Audit modifiche", "Change audit", "Änderungs-Audit")}</h3>
          {data.audit.length === 0 ? <p className="text-sm text-slate-500">—</p> : (
            <div className="space-y-1.5" data-testid="cd-audit">{data.audit.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                <span className="text-slate-300">{a.action} · <span className="text-slate-500">{a.entity_type}</span></span>
                <span className="text-xs text-slate-600">{formatDateTime(a.timestamp)}</span>
              </div>
            ))}</div>
          )}
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return <Card className="p-4"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-mono text-lg text-white" data-testid={testid}>{value}</p></Card>;
}

function TxList({ rows, L, empty, kind }) {
  if (!rows || rows.length === 0) return <Card className="p-6"><p className="text-sm text-slate-500">{empty}</p></Card>;
  return (
    <Card className="p-6"><div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3 text-sm" data-testid={`cd-${kind}-${r.id}`}>
          <div><p className="text-white">{formatCurrency(r.amount, r.currency || r.asset)} · <span className="text-slate-500">{r.network || r.method || ""}</span></p>
            <p className="text-xs text-slate-500 font-mono break-all">{r.tx_hash || r.payment_reference || r.destination || "—"} · {formatDateTime(r.created_at)}</p></div>
          <span className="text-xs text-gold shrink-0 ml-2">{r.status}</span>
        </div>
      ))}
    </div></Card>
  );
}
